// ══════════════════════════════════════════════════════
// Strategy Lab — Diff Preview Modal
// ══════════════════════════════════════════════════════

import { theme, fonts } from "../../theme";
import { Card, Button, Pill } from "../../components/ui";

/**
 * DiffPreview — Shows a modal with before/after changes
 * @param {Object} props
 * @param {string} props.title - e.g. "Phase 1 übernehmen"
 * @param {string} props.description - What this apply does
 * @param {string} props.stratFile - Target YAML filename
 * @param {Array} props.changes - [{key, oldVal, newVal, section}]
 * @param {Function} props.onConfirm
 * @param {Function} props.onCancel
 */
export default function DiffPreview({ title, description, stratFile, changes, onConfirm, onCancel }) {
  if (!changes || changes.length === 0) return null;

  const sections = {};
  changes.forEach(c => {
    const sec = c.section || "Allgemein";
    if (!sections[sec]) sections[sec] = [];
    sections[sec].push(c);
  });

  return <div style={{
    position:"fixed", inset:0, zIndex:9998,
    display:"flex", alignItems:"center", justifyContent:"center",
    background:"rgba(0,0,0,0.35)", backdropFilter:"blur(3px)",
  }} onClick={onCancel}>
    <div onClick={e => e.stopPropagation()} style={{
      background:theme.white, borderRadius:16,
      maxWidth:560, width:"92%", maxHeight:"80vh",
      boxShadow:"0 20px 60px rgba(0,0,0,0.15)",
      display:"flex", flexDirection:"column", overflow:"hidden",
    }}>
      {/* Header */}
      <div style={{
        padding:"20px 24px 16px",
        borderBottom:`1px solid ${theme.divider}`,
      }}>
        <div style={{ fontSize:16, fontWeight:800, color:theme.text, marginBottom:4 }}>{title}</div>
        <div style={{ fontSize:12, color:theme.text3, lineHeight:1.5 }}>{description}</div>
        <div style={{
          display:"inline-flex", alignItems:"center", gap:6,
          padding:"4px 10px", borderRadius:6,
          background:theme.blueLight, marginTop:8,
        }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={theme.blueText} strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z"/><polyline points="14 2 14 8 20 8"/></svg>
          <span style={{ fontSize:11, fontWeight:700, fontFamily:fonts.mono, color:theme.blueText }}>{stratFile}</span>
        </div>
      </div>

      {/* Changes List */}
      <div style={{ flex:1, overflow:"auto", padding:"12px 24px 16px" }}>
        <div style={{ fontSize:10, fontWeight:700, color:theme.text4, textTransform:"uppercase", letterSpacing:"0.8px", marginBottom:10 }}>
          {changes.length} Änderung{changes.length !== 1 ? "en" : ""}
        </div>

        {Object.entries(sections).map(([sec, items]) => (
          <div key={sec} style={{ marginBottom:14 }}>
            <div style={{
              fontSize:10, fontWeight:700, color:theme.text3,
              textTransform:"uppercase", letterSpacing:"0.5px",
              marginBottom:6, paddingBottom:4,
              borderBottom:`1px solid ${theme.divider}`,
            }}>{sec}</div>
            {items.map((c, i) => (
              <div key={i} style={{
                display:"flex", alignItems:"center", gap:10,
                padding:"7px 10px", marginBottom:2,
                borderRadius:6,
                background: c.oldVal != null ? "rgba(37,99,235,0.03)" : theme.greenLight,
              }}>
                {/* Key name */}
                <span style={{ flex:"0 0 140px", fontSize:11, fontWeight:600, color:theme.text2, fontFamily:fonts.mono }}>
                  {c.key}
                </span>

                {/* Old value */}
                {c.oldVal != null ? (
                  <span style={{
                    flex:1, fontSize:11, fontFamily:fonts.mono,
                    color:theme.redText, textDecoration:"line-through",
                    opacity:0.7,
                  }}>
                    {formatVal(c.oldVal)}
                  </span>
                ) : (
                  <span style={{ flex:1, fontSize:11, fontFamily:fonts.mono, color:theme.text4 }}>—</span>
                )}

                {/* Arrow */}
                <span style={{ color:theme.text4, fontSize:10 }}>→</span>

                {/* New value */}
                <span style={{
                  flex:1, fontSize:11, fontWeight:700, fontFamily:fonts.mono,
                  color: theme.greenText,
                }}>
                  {formatVal(c.newVal)}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{
        display:"flex", gap:8, justifyContent:"flex-end",
        padding:"14px 24px", borderTop:`1px solid ${theme.divider}`,
        background:"rgba(0,0,0,0.015)",
      }}>
        <Button onClick={onCancel}>Abbrechen</Button>
        <Button primary onClick={onConfirm}>
          Übernehmen ({changes.length})
        </Button>
      </div>
    </div>
  </div>;
}

function formatVal(v) {
  if (v === true) return "✓ an";
  if (v === false) return "✗ aus";
  if (typeof v === "number") return v % 1 === 0 ? String(v) : v.toFixed(2);
  return String(v);
}

/**
 * Build changes array from best params → structured sections
 * If currentConfig is provided, extract old values for real Before/After diff
 */
export function buildDiffFromParams(params, currentConfig) {
  const changes = [];
  for (const [section, vals] of Object.entries(params)) {
    if (!vals || typeof vals !== "object") continue;
    for (const [key, val] of Object.entries(vals)) {
      if (val == null) continue;
      const oldVal = currentConfig ? extractOldVal(currentConfig, section, key) : null;
      changes.push({ key, newVal:val, oldVal, section:sectionLabel(section) });
    }
  }
  return changes;
}

export function buildDiffFromConsensus(fixItems, narrowItems, consSel, currentConfig) {
  const changes = [];
  fixItems.filter(it => consSel[it.key]).forEach(it => {
    const newV = it.value === "True" || it.value === "true" ? true : it.value === "False" || it.value === "false" ? false : it.value;
    const oldVal = currentConfig ? extractConsensusOldVal(currentConfig, it.key) : null;
    changes.push({ key: it.key, newVal: newV, oldVal, section: "Fixiert (≥80% Confidence)" });
  });
  narrowItems.filter(it => consSel[it.key]).forEach(it => {
    changes.push({
      key: it.key, newVal: `[${it.min} – ${it.max}]`, oldVal: null,
      section: "Ranges eingeengt (P10–P90)",
    });
  });
  return changes;
}

/** Extract old value from nested config by section + key */
function extractOldVal(cfg, section, key) {
  if (!cfg) return null;
  const secMap = {
    take_profit: cfg.take_profit, stop_loss: cfg.stop_loss,
    regime_filter: cfg.regime_filter, ema_filter: cfg.ema_filter,
    break_even: cfg.break_even, trailing: cfg.trailing,
    structure: cfg.structure, risk: cfg.risk, timeframes: cfg.timeframes,
    fvg: cfg.fvg, loss_scaling: cfg.loss_scaling,
    entry: cfg.entry, bad_hours: cfg.bad_hours,
  };
  const sec = secMap[section];
  if (!sec) return null;
  // Direct key match
  if (sec[key] != null) return sec[key];
  // Try common aliases
  const aliases = {
    rr1:"rr1", rr2:"rr2", rr3:"rr3", rr4:"rr4",
    atr_mult_min:"atr_mult_min", atr_mult_max:"atr_mult_max",
    after_rr:"after_rr", atr_mult:"atr_mult", activation_rr:"activation_rr",
    adx_threshold:"adx_threshold", atr_sma_length:"atr_sma_length",
    context_swing_length:"context_swing_length", entry_swing_length:"entry_swing_length",
    max_daily_trades:"max_daily_trades", cooldown_bars:"cooldown_bars",
  };
  if (aliases[key] && sec[aliases[key]] != null) return sec[aliases[key]];
  return null;
}

/** Extract old value for consensus categorical keys */
function extractConsensusOldVal(cfg, key) {
  if (!cfg) return null;
  const map = {
    regime_enabled: cfg.regime_filter?.enabled,
    ema_enabled: cfg.ema_filter?.enabled,
    fvg_enabled: cfg.fvg?.enabled,
    be_enabled: cfg.break_even?.enabled,
    trailing_enabled: cfg.trailing?.enabled ?? (cfg.trailing?.mode ? true : null),
    loss_scaling_enabled: cfg.loss_scaling?.enabled,
    sl_mode: cfg.stop_loss?.mode,
    entry_mode: cfg.structure?.entry_mode,
    break_confirm: cfg.structure?.break_confirm,
    regime_mode: cfg.regime_filter?.mode,
    direction_filter: cfg.structure?.direction_filter,
    require_htf_choch: cfg.structure?.require_htf_choch,
    require_htf_trend: cfg.structure?.require_htf_trend,
  };
  return map[key] ?? null;
}

function sectionLabel(sec) {
  const map = {
    take_profit:"Take Profit", stop_loss:"Stop Loss", regime_filter:"Regime Filter",
    ema_filter:"EMA Filter", break_even:"Break Even", trailing:"Trailing",
    structure:"Struktur", risk:"Risk", timeframes:"Timeframes",
    fvg:"FVG", loss_scaling:"Loss Scaling",
    entry:"Entry", bad_hours:"Bad Hours",
  };
  return map[sec] || sec;
}
